import { z } from "zod"

// Common search params used for pagination, sorting and filtering.
export const ORDER_BY_PARAM = "orderBy"
export const FILTER_BY_PARAM = "filter"
export const SKIP_PARAM = "skip"
export const TOP_PARAM = "top"

// Pagination
export const DEFAULT_SKIP = 0
export const DEFAULT_TOP = 12

// Schemas
const nonNegativeIntSchema = z.coerce.number().int().nonnegative()

export function getOptions<T extends string[], K extends string[]>(args: {
  url: string
  orderByOptions?: T
  filterByOptions?: K
}): Options<T, K> {
  const { orderByOptions, filterByOptions } = args
  const url = new URL(args.url)

  // Order By
  const orderByParam = url.searchParams.get(ORDER_BY_PARAM)
  const orderBy = orderByOptions?.find((option) => option === orderByParam)

  // Top
  const topParam = url.searchParams.get(TOP_PARAM)
  const parsedTop = nonNegativeIntSchema.safeParse(topParam)
  const top = parsedTop.success ? parsedTop.data : undefined

  // Skip
  const skipParam = url.searchParams.get(SKIP_PARAM)
  const parsedSkip = nonNegativeIntSchema.safeParse(skipParam)
  const skip = parsedSkip.success ? parsedSkip.data : undefined

  // Filter By
  const filterByParam = url.searchParams.get(FILTER_BY_PARAM)
  const filterBy = filterByOptions?.find((option) => option === filterByParam)

  return { orderBy, top, skip, filterBy }
}

export function buildUrlWithQueryOptions(
  pathname: string,
  queryOptions: Options<string[], string[]>,
) {
  const { top, skip, orderBy, filterBy } = queryOptions

  const search = new URLSearchParams()

  if (top) search.set(TOP_PARAM, top.toString())
  if (skip) search.set(SKIP_PARAM, skip.toString())
  if (orderBy) search.set(ORDER_BY_PARAM, orderBy)
  if (filterBy) search.set(FILTER_BY_PARAM, filterBy)

  return `${pathname}?${search.toString()}`
}

export function getPaginationData(args: {
  resultCount: number
  pathname: string
  queryOptions: {
    top: number
    skip: number
    orderBy?: string
    filterBy?: string
  }
}): PaginationData {
  const { pathname, resultCount, queryOptions } = args

  const hasNextPage = resultCount >= queryOptions.top
  if (!hasNextPage) return { hasNextPage, nextPageUrl: null }

  return {
    hasNextPage,
    nextPageUrl: buildUrlWithQueryOptions(pathname, {
      ...queryOptions,
      skip: queryOptions.skip + queryOptions.top,
    }),
  }
}

// Types

export type Options<T extends string[], K extends string[]> = {
  orderBy?: T[number]
  filterBy?: K[number]
  top?: number
  skip?: number
}

export type PaginationData =
  | { hasNextPage: true; nextPageUrl: string }
  | { hasNextPage: false; nextPageUrl: null }
